import { defineStore } from 'pinia';
import api from '@/services/api';

export const useOrganizationStore = defineStore('organization', {
  state: () => ({
    organizations: [],
    organizationUsers: [],
    organizationDomains: [],
    pagination: {},
    loading: false,
    usersLoading: false,
    domainsLoading: false,
    error: null,
    roles: [],
  }),
  actions: {
    async fetchOrganizations() {
      this.loading = true;
      this.error = null;
      try {
        const response = await api.getOrganizations();
        this.organizations = response.data;
      } catch (error) {
        this.error = error;
      } finally {
        this.loading = false;
      }
    },
    async addOrganization(organization) {
      this.loading = true;
      this.error = null;
      try {
        await api.createOrganization(organization);
        await this.fetchOrganizations(); // Refresh the list
      } catch (error) {
        this.error = error;
        throw error;
      } finally {
        this.loading = false;
      }
    },
    async editOrganization(id, organization) {
      this.loading = true;
      this.error = null;
      try {
        await api.updateOrganization(id, organization);
        await this.fetchOrganizations(); // Refresh the list
      } catch (error) {
        this.error = error;
        throw error;
      } finally {
        this.loading = false;
      }
    },
    async removeOrganization(id) {
      this.loading = true;
      this.error = null;
      try {
        await api.deleteOrganization(id);
        await this.fetchOrganizations(); // Refresh the list
      } catch (error) {
        this.error = error;
        throw error;
      } finally {
        this.loading = false;
      }
    },

    async fetchUsersForOrganization(orgId, page = 1, perPage = 10, sortField = 'created_at', sortOrder = -1) {
      this.usersLoading = true;
      this.error = null;
      try {
        const response = await api.getUsersForOrganization(orgId, page, perPage, sortField, sortOrder);
        this.organizationUsers = response.data.users;
        this.pagination = {
          total: response.data.total,
          pages: response.data.pages,
          currentPage: response.data.current_page,
        };
      } catch (error) {
        this.error = error;
      } finally {
        this.usersLoading = false;
      }
    },

    async bulkUpdateUsers(orgId, users) {
      this.error = null;
      try {
        await api.bulkUpdateUsers(orgId, users);
        await this.fetchUsersForOrganization(orgId, this.pagination.currentPage);
      } catch (error) {
        this.error = error;
        throw error;
      }
    },

    async deleteUser(orgId, userId) {
      this.error = null;
      try {
        await api.deleteUser(userId);
        await this.fetchUsersForOrganization(orgId, this.pagination.currentPage);
      } catch (error) {
        this.error = error;
        throw error;
      }
    },

    async fetchDomainsForOrganization(orgId) {
      this.domainsLoading = true;
      this.error = null;
      try {
        const response = await api.getDomainsForOrganization(orgId);
        this.organizationDomains = response.data;
      } catch (error) {
        this.error = error;
      } finally {
        this.domainsLoading = false;
      }
    },

    async addDomainForOrganization(orgId, domainName) {
      this.error = null;
      try {
        await api.addDomainForOrganization(orgId, domainName);
        await this.fetchDomainsForOrganization(orgId); // Refresh
      } catch (error) {
        this.error = error;
        throw error; // Re-throw to be caught in the component
      }
    },

    async deleteDomain(orgId, domainId) {
      this.error = null;
      try {
        await api.deleteDomain(domainId);
        await this.fetchDomainsForOrganization(orgId); // Refresh
      } catch (error) {
        this.error = error;
        throw error;
      }
    },

    async fetchRoles() {
      this.error = null;
      try {
        const response = await api.getRoles();
        this.roles = response.data;
      } catch (error) {
        this.error = error;
        console.error('Failed to fetch roles:', error);
        throw error;
      }
    },
  },
});
